import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Select } from '../../../../components/ui/Select';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { cn } from '../../../../utils/cn';
import ReminderMessagePreview from './ReminderMessagePreview';
import { CHANNEL_LABELS } from '../utils/collectionReminders';
import { messageTemplateName, previewSkippedCounts, reminderPreviewErrorText } from '../utils/reminderTemplates';

const asList = (value) => (Array.isArray(value) ? value : []);

/**
 * Columna de vista previa de "Enviar recordatorio" y "Programar": el mensaje que armaría
 * billing HOY con la plantilla elegida, por canal (burbuja de WhatsApp o correo aislado).
 *
 * Se rotula "con los datos de hoy" porque no anticipa lo que cambie hasta el envío: notas
 * que se paguen, otras pendientes del comercio que salgan en el mismo mensaje o el
 * enfriamiento de 24 h.
 *
 * Con `companyPicker` (Programar, donde la selección cruza comercios) se elige de qué
 * comercio ver el mensaje, entre los que billing dice que lo recibirían. En Programar,
 * billing ya descuenta las notas que esperan en otro programado cercano por el mismo canal
 * (`skipped` con reason 'already_scheduled'): se avisan aparte.
 *
 * @param {{ preview: ReturnType<typeof import('../hooks/useReminderMessagePreview').default>,
 *   companyPicker?: { value: string|null, onChange: (companyId: string) => void } }} props
 */
export default function ReminderSendPreview({ preview, companyPicker }) {
  const { t } = useTranslation();
  const baseId = useId();
  const [shownChannel, setShownChannel] = useState(null);
  const { data, error, pending, idle, refresh } = preview;

  const items = asList(data?.items);
  const companies = asList(data?.companies);
  const skipped = previewSkippedCounts(data?.skipped);
  const itemChannels = items.map((item) => item.channel);
  const channel = itemChannels.includes(shownChannel) ? shownChannel : itemChannels[0] ?? null;
  const item = items.find((i) => i.channel === channel) ?? null;
  // El comercio elegido se muestra en el selector aunque su mensaje todavía no llegó.
  const picked = companies.some((c) => c.companyId === companyPicker?.value) ? companyPicker.value : null;
  const shownCompanyId = picked ?? item?.companyId ?? '';

  let content;
  if (idle === 'no_channels') {
    content = <p className="m-0 text-sm text-fg-muted">{t('Elegí al menos un canal para ver el mensaje.')}</p>;
  } else if (idle === 'no_notes') {
    content = <p className="m-0 text-sm text-fg-muted">{t('No hay notas cobrables para armar el mensaje.')}</p>;
  } else {
    content = (
      <>
        {error && (
          <p className="m-0 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {reminderPreviewErrorText(error, t)}
          </p>
        )}
        {!data && !error && (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}
        {/* Tras un error queda la última vista previa buena, atenuada: ya no es la del
            formulario actual (p. ej. se eligió una plantilla que billing rechazó). */}
        {data && (
          <div className={cn('space-y-3 transition-opacity', (pending || error) && 'opacity-60')}>
            {companyPicker && companies.length > 0 && (
              <div className="space-y-1">
                <Select
                  label={t('Ver mensaje de')}
                  value={shownCompanyId}
                  onChange={(e) => companyPicker.onChange(e.target.value)}
                >
                  {companies.map((company) => (
                    <option key={company.companyId} value={company.companyId}>
                      {/* Nombre del comercio: dato, fuera de t(). */}
                      {`${company.companyName || t('Sin nombre')} · ${t('{{n}} notas', { n: company.notes })}`}
                    </option>
                  ))}
                </Select>
                <p className="m-0 text-xs text-fg-muted">
                  {companies.length === 1
                    ? t('Un comercio recibiría el mensaje.')
                    : t('{{n}} comercios recibirían un mensaje, cada uno con sus notas.', { n: companies.length })}
                </p>
              </div>
            )}

            {companies.length === 0 && (
              <p className="m-0 text-sm text-fg-muted">
                {skipped.alreadyScheduled > 0
                  ? t('No saldría ningún mensaje: las notas ya no son cobrables o ya tienen un recordatorio pendiente por el mismo canal en otro programado.')
                  : t('Hoy no saldría ningún mensaje: las notas ya no son cobrables.')}
              </p>
            )}

            {itemChannels.length > 1 && (
              <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('Canal de la vista previa')}>
                {itemChannels.map((value) => (
                  <Button
                    key={value}
                    size="sm"
                    variant={channel === value ? 'primary' : 'outline'}
                    aria-pressed={channel === value}
                    onClick={() => setShownChannel(value)}
                  >
                    {t(CHANNEL_LABELS[value] ?? value)}
                  </Button>
                ))}
              </div>
            )}

            {item && (
              <div className="space-y-2">
                <p className="m-0 text-xs text-fg-muted">
                  {t('Plantilla')}:{' '}
                  {/* Nombre de la plantilla: texto del operador, fuera de t(). */}
                  <span className="font-medium text-fg">{messageTemplateName(item.template, t)}</span>
                </p>
                <ReminderMessagePreview
                  channel={item.channel}
                  subject={item.subject}
                  html={item.html}
                  text={item.text}
                />
                <p className="m-0 text-xs text-fg-muted">
                  {item.channel === 'whatsapp' && Number.isInteger(item.max)
                    ? `${t('Largo del mensaje: {{n}} de {{max}} caracteres.', { n: item.length ?? 0, max: item.max })} `
                    : ''}
                  {t('Notas en el mensaje: {{listed}} de {{total}}.', {
                    listed: item.listedNotes ?? 0,
                    total: item.totalNotes ?? 0,
                  })}
                </p>
                {item.exceedsLimit && (
                  <p className="m-0 flex items-start gap-1.5 rounded-md bg-status-warning-soft px-2.5 py-1.5 text-xs text-status-warning" role="alert">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>
                      {t('Este WhatsApp pasa del máximo de {{max}} caracteres y no se enviaría. Elegí otra plantilla o destildá WhatsApp.', {
                        max: item.max,
                      })}
                    </span>
                  </p>
                )}
              </div>
            )}

            {skipped.other > 0 && companies.length > 0 && (
              <p className="m-0 text-xs text-fg-muted">
                {t('{{n}} notas no entrarían: ya no son cobrables o no existen.', { n: skipped.other })}
              </p>
            )}
            {skipped.alreadyScheduled > 0 && companies.length > 0 && (
              <p className="m-0 text-xs text-fg-muted">
                {t('{{n}} notas ya tienen un recordatorio pendiente por el mismo canal en otro programado: por ese canal no entran en este.', {
                  n: skipped.alreadyScheduled,
                })}
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <section className="min-w-0 space-y-3 rounded-lg border border-border bg-bg p-4" aria-labelledby={`${baseId}-title`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`${baseId}-title`} className="m-0 text-sm font-semibold text-fg">
          {t('Vista previa con los datos de hoy')}
        </h3>
        <div className="flex items-center gap-2">
          {pending && (
            <span className="text-xs text-fg-muted" role="status">
              {t('Actualizando…')}
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<RefreshCw className="h-4 w-4" />}
            disabled={!!idle}
            onClick={refresh}
          >
            {t('Actualizar')}
          </Button>
        </div>
      </div>
      {content}
      <p className="m-0 text-xs text-fg-muted">
        {t('Al enviarse puede cambiar: si se pagan notas, si se junta con otros recordatorios pendientes del comercio o si ya recibió uno en las últimas 24 h.')}
      </p>
    </section>
  );
}
